import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, Shield, X, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";

type Member = {
  id: string;
  name: string;
  phone: string;
  is_group_admin: boolean;
};

export function GroupAdminsSelector({ groupId }: { groupId: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["group-members-admins", groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_members")
        .select("id, name, phone, is_group_admin")
        .eq("group_id", groupId)
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Member[];
    },
  });

  const toggleMut = useMutation({
    mutationFn: async ({ memberId, makeAdmin }: { memberId: string; makeAdmin: boolean }) => {
      const { error } = await supabase
        .from("syra_members")
        .update({ is_group_admin: makeAdmin } as any)
        .eq("id", memberId);
      if (error) throw error;
    },
    onMutate: async ({ memberId, makeAdmin }) => {
      await qc.cancelQueries({ queryKey: ["group-members-admins", groupId] });
      const prev = qc.getQueryData<Member[]>(["group-members-admins", groupId]);
      qc.setQueryData<Member[]>(["group-members-admins", groupId], (old) =>
        (old ?? []).map((m) => (m.id === memberId ? { ...m, is_group_admin: makeAdmin } : m)),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["group-members-admins", groupId], ctx.prev);
      toast({ title: "Erro ao atualizar admin", variant: "destructive" });
    },
    onSuccess: (_d, vars) => {
      toast({ title: vars.makeAdmin ? "Admin promovido" : "Admin removido" });
    },
  });

  const admins = members.filter((m) => m.is_group_admin);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground flex items-center gap-2">
        <Shield className="w-4 h-4 text-primary" /> Admins do grupo
      </label>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={isLoading}
          >
            <span className="truncate text-left flex-1">
              {isLoading
                ? "Carregando membros..."
                : admins.length === 0
                ? "Selecionar admins..."
                : `${admins.length} admin(s) selecionado(s)`}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Buscar membro pelo nome..." />
            <CommandList>
              <CommandEmpty>Nenhum membro encontrado.</CommandEmpty>
              <CommandGroup>
                {members.map((m) => (
                  <CommandItem
                    key={m.id}
                    value={m.name || m.phone}
                    onSelect={() => toggleMut.mutate({ memberId: m.id, makeAdmin: !m.is_group_admin })}
                    className="flex items-center gap-2"
                  >
                    <Checkbox
                      checked={m.is_group_admin}
                      onCheckedChange={() => toggleMut.mutate({ memberId: m.id, makeAdmin: !m.is_group_admin })}
                      onClick={(e) => e.stopPropagation()}
                      className="shrink-0"
                    />
                    <span className="text-sm truncate flex-1">{m.name || "Sem nome"}</span>
                    {m.is_group_admin && (
                      <Badge variant="secondary" className="text-[10px] shrink-0">Admin</Badge>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected admins chips */}
      {admins.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {admins.map((m) => (
            <Badge key={m.id} variant="secondary" className="gap-1 pr-1 text-xs">
              <Shield className="h-3 w-3" />
              {m.name || m.phone}
              <button
                type="button"
                onClick={() => toggleMut.mutate({ memberId: m.id, makeAdmin: false })}
                className="ml-1 rounded-sm hover:bg-background/60 p-0.5"
                aria-label={`Remover ${m.name} como admin`}
              >
                {toggleMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
              </button>
            </Badge>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Admins podem usar comandos restritos (como <code className="bg-muted px-1 rounded">/kb</code>) e moderar a IA neste grupo.
      </p>
    </div>
  );
}
